import { useEffect, useMemo, useRef, useState } from "react";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { ExpenseDashBoardStore } from "../../../../../store/main/ExpenseDashBoardStore";
import { ReportFilters } from "../../../../../domain/models/report-filters";
import Roles from "../../../../../common/enums/Roles";
import moment from "moment";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ReportFiltersComponent from "../../ReportFiltersComponent";
import ReportFiltersLoading from "../../ReportFiltersLoading";
import ReportScreen from "../../../../../common/enums/ReportScreens";
import MissingLocationStatusModal from "../Modal/MissingLocationStatusModal";
import WarningIcon from "@mui/icons-material/Warning";
import AccessCheckStatusModal from "../Modal/AccessCheckStatusModal";
import SubmitConfirmationModal from "../Modal/SubmitConfirmationModal";
import ExpenseSummaryCard from "./ExpenseSummaryCard";
import Swal from "sweetalert2";
import FileUploadReadModal from "../Modal/FileUploadReadModal";

type ExpenseRow = {
  id?: number;
  uploadedFiles: UploadedFile[];
  is_error: boolean;
  date: string;
  workingBrick: string;
  station: string;
  locality: string;
  totalDistanceKm: number;
  dailyAllowance: number;
  travelAllowance: number;
  fixAllowance: number | null;
  foodAllowance: number | null;
  hillStationAllowance: number | null;
  total: string;
  status: "correct" | "incorrect";
  approvedStatus: "approved" | "pending";
  systemRemarks: string;
  remark: string | "";
  expectedAmount: number | null;
  attendanceType: string | null;
  totalAmount?: number | null;
  totalFinalAmount: string;
  miscellaneousExpenseType?: number | null;
  miscellaneousExpenseAmount?: number | null;
  approvedBy?: number | null;
  approverName?: string | null;
};

type ExpenseRowData = {
  id: number;
  reporting_date: string;
  city_category: string;
  brick_type: string;
  total_distance_travel_km: string;
  daily_allowance: string;
  traveling_allowance: string;
  fix_allowance: string;
  food_allowance: string;
  hill_station_allowance: string;
  remark: string;
  status: string;
  expected_amount: string;
  approved_status: string;
  system_calculated_amount: string;
  system_remarks: string;
  brick_name: string;
  final_amount: string;
  attendance_type: string;
  approved_by: number | null;
  approver_name: string | null;
  miscellaneous_expense_type?: string | null;
  miscellaneous_expense_amount?: string | null;
  expense_uploaded_files: UploadedFile[];
};

type UploadedFile = {
  id: number;
  expense_id: number;
  file_uuid: string;
  uploaded_by: number;
  expense_date: string;
  status: string;
};

type MonthlyExpense = {
  user_id: number;
  month_year: number;
  total_distance_travel_km: string;
  total_daily_allowance: string;
  total_traveling_allowance: string;
  total_fix_allowance: string;
  total_food_allowance: string;
  system_calculated_amount: string;
  final_amount: string;
  total_hill_station_allowance: string;
  total_expense_amount: string;
  total_missed_working_days: string;
  total_working_days: number;
  approver_name: string;
};

type UserExpenseSummary = {
  user_id: number;
  user_name: string;
  is_remark_locked: boolean;
  is_remark_submitted: boolean;
  max_expense_limit: string;
  daily_expenses: ExpenseRowData[];
  monthly_expense: MonthlyExpense[];
};

type ExpenseApiResponse = {
  success: boolean;
  user_expense_summary: UserExpenseSummary;
};

type AccessCheckStatus = {
  is_remark_locked: boolean | undefined;
  is_remark_submitted: boolean | undefined;
  button_access: number;
};

type UserRemarksData = {
  month: string;
  year: number;
  add_remarks: AddRemark[];
};

type AddRemark = {
  id: number;
  reporting_date: string;
  remark: string;
  status: string;
  expected_amount: string | null;
  miscellaneous_expense_type_id?: number | null;
  miscellaneous_expense_amount?: number | null;
};

type ExpenseAPIResponse = {
  success: boolean;
  miscellaneous_expense_types: ExpenseReason[];
};

type ExpenseReason = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
};

const commonTableStyle = {
  padding: "2px 4px",
  fontSize: "0.75rem",
  lineHeight: 1.0,
  textAlign: "left",
};

const UserExpenseReport = () => {
  const { userDetails, userRole, decodeToken } = CustomerDatabaseStore();
  const {
    fetchMonthlyExpenseReport,
    FetchMonthlyExpenseReportState,
    FetchMonthlyExpenseReport,
    addCommentAmountExpenseReport,
    AddCommentAmountExpenseReportState,
    FetchAddExpenseReason,
    FetchAddExpenseReasonState,
    fetchAddExpenseReason,
  } = ExpenseDashBoardStore();

  const currentDate = new Date();

  const isHeadquarterManager = userRole.role === Roles.HEADQUATER_MANAGER;
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1);
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const currentMonthName = new Date(year, month - 1).toLocaleString("default", {
    month: "long",
  });
  const previousMonthDate = new Date(currentDate);
  previousMonthDate.setMonth(currentDate.getMonth() - 1);
  const prevMonthName = previousMonthDate.toLocaleString("default", {
    month: "long",
  });
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [tableApiData, setTableApiData] = useState<ExpenseRow[]>([]);
  const [fetchApiResponse, setFetchApiResponse] =
    useState<ExpenseApiResponse | null>(null);
  const [openSystemRemarkModal, setOpenSystemRemarkModal] =
    useState<boolean>(false);
  const [noAccessModalOpen, setNoAccessModalOpen] = useState(false);
  const [isConfirmationSubmitModalOpen, setIsConfirmationSubmitModalOpen] =
    useState<boolean>(false);
  const [addRemarks, setAddRemarks] = useState<AddRemark[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [apiLoading, setApiLoading] = useState<boolean>(true);
  const [isDownloadAllowed, setIsDownloadAllowed] = useState<boolean>(false);
  const [showTotalFinalAmount, setShowTotalFinalAmount] =
    useState<boolean>(false);
  const [selectedSystemRemarks, setSelectedSystemRemarks] =
    useState<string>("");
  const [rowErrors, setRowErrors] = useState<
    Record<number, { remark?: string; amount?: string }>
  >({});
  const [touchedRows, setTouchedRows] = useState<
    Record<number, { remark?: boolean; amount?: boolean }>
  >({});
  const [showExpectedAmountColumn, setShowExpectedAmountColumn] =
    useState<boolean>(true);
  const [showMiscellaneousAmountColumn, setShowMiscellaneousAmountColumn] =
    useState<boolean>(true);
  const [expenseReasons, setExpenseReasons] = useState<ExpenseReason[]>([]);
  const [miscErrors, setMiscErrors] = useState<Record<number, string>>({});
  const [overallTotalAmount, setOverallTotalAmount] = useState<number>(0);
  const [systemTotalAmount, setSystemTotalAmount] = useState<number>(0);
  const [openMyModal, setOpenMyModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
    uuids: string[];
  } | null>(null);

  const lastWarningMessage = useRef<"none" | "10%" | "15%">("none");

  const handleOpenMyModal = (row: ExpenseRow) => {
    setSelectedRow({
      id: row.id!,
      uuids: row.uploadedFiles?.map((f) => f.file_uuid) ?? [],
    });

    setOpenMyModal(true);
  };

  const handleCloseMyModal = () => {
    setOpenMyModal(false);
  };

  useEffect(() => {
    if (!openMyModal) {
      refreshTableAfterModalClose();
    }
  }, [openMyModal]);

  const refreshTableAfterModalClose = async () => {
    const monthString = month.toString().padStart(2, "0");
    const response = await fetchMonthlyExpenseReport({
      month: monthString,
      year,
    });

    if (!response?.success) return;

    const fetchData = response.user_expense_summary.daily_expenses;

    const mappedApiData = fetchData.map((expense: ExpenseRowData) => ({
      id: expense.id,
      uploadedFiles: expense.expense_uploaded_files ?? [],
      date: expense.reporting_date,
      workingBrick: expense.brick_name ?? "-",
      station: expense.brick_type ?? "-",
      locality: expense.city_category ?? "-",
      totalDistanceKm: parseFloat(expense.total_distance_travel_km),
      dailyAllowance: parseFloat(expense.daily_allowance),
      travelAllowance: parseFloat(expense.traveling_allowance),
      fixAllowance: expense.fix_allowance
        ? parseFloat(expense.fix_allowance)
        : null,
      foodAllowance: expense.food_allowance
        ? parseFloat(expense.food_allowance)
        : null,
      hillStationAllowance: expense.hill_station_allowance
        ? parseFloat(expense.hill_station_allowance)
        : null,
      attendanceType: expense.attendance_type ?? "-",
      total: Number(expense.system_calculated_amount).toFixed(2),
      status: expense.status,
      approvedStatus: expense.approved_status,
      systemRemarks: expense.system_remarks ?? "",
      remark: expense.remark ?? "",
      expectedAmount: expense.expected_amount
        ? parseFloat(expense.expected_amount)
        : null,
      totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
      miscellaneousExpenseType: expense.miscellaneous_expense_type ?? null,
      miscellaneousExpenseAmount: expense.miscellaneous_expense_amount ?? null,
    }));

    // 🔥 Preserve local user edits
    setTableApiData((prev) => mergeLocalEdits(mappedApiData));

    // Update selected row
    if (selectedRow?.id) {
      const updatedRow = mappedApiData.find(
        (r: any) => r.id === selectedRow.id
      );
      if (updatedRow) {
        setSelectedRow({
          id: updatedRow.id,
          uuids: updatedRow.uploadedFiles.map((f: any) => f.file_uuid),
        });
      }
    }
  };

  const mergeLocalEdits = (newData: ExpenseRow[]) => {
    const updated = newData.map((newRow) => {
      const oldRow = tableApiData.find((r) => r.id === newRow.id);

      if (!oldRow) return newRow;

      return {
        ...newRow,
        remark: oldRow.remark,
        expectedAmount: oldRow.expectedAmount,
        miscellaneousExpenseType: oldRow.miscellaneousExpenseType,
        miscellaneousExpenseAmount: oldRow.miscellaneousExpenseAmount,
        status: oldRow.status,
      };
    });
    return updated;
  };

  const OVERALL_15_THRESHOLD = 1.15;
  const OVERALL_10_THRESHOLD = 1.1;

  useEffect(() => {
    const maxLimit = fetchApiResponse?.user_expense_summary?.max_expense_limit;
    const isRemarkSubmitted =
      fetchApiResponse?.user_expense_summary?.is_remark_submitted;

    if (!maxLimit || isRemarkSubmitted) return;

    const avgAmount = parseFloat(maxLimit);
    if (overallTotalAmount > avgAmount) {
      const threshold10 = systemTotalAmount * OVERALL_10_THRESHOLD;
      const threshold15 = systemTotalAmount * OVERALL_15_THRESHOLD;
      if (overallTotalAmount > threshold15) {
        if (lastWarningMessage.current !== "15%") {
          Swal.fire({
            icon: "warning",
            title: "High Expense Warning",
            text: `Your total exceeds the system-calculated amount by more than 15%. Submission is now disabled.`,
            confirmButtonColor: "#d33",
          });
          lastWarningMessage.current = "15%";
        }
      } else if (overallTotalAmount > threshold10) {
        if (lastWarningMessage.current !== "10%") {
          Swal.fire({
            icon: "warning",
            title: "Expense Warning",
            text: `Your total exceeds the system-calculated amount by more than 10%.`,
            confirmButtonColor: "#d33",
          });
          lastWarningMessage.current = "10%";
        }
      } else {
        lastWarningMessage.current = "none";
      }
    } else {
      lastWarningMessage.current = "none";
    }
  }, [
    overallTotalAmount,
    systemTotalAmount,
    fetchApiResponse?.user_expense_summary?.max_expense_limit,
    fetchApiResponse?.user_expense_summary?.is_remark_submitted,
  ]);

  const isOver15Percent = useMemo(() => {
    if (!systemTotalAmount) return false;
    return overallTotalAmount > systemTotalAmount * OVERALL_15_THRESHOLD;
  }, [overallTotalAmount, systemTotalAmount]);

  useEffect(() => {
    calculateOverallTotal(tableApiData);
  }, [tableApiData]);

  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    decodeToken();
    loadExpenseReasons();
  }, []);

  const loadExpenseReasons = async () => {
    try {
      const response = await fetchAddExpenseReason();
      if (response.success) {
        setExpenseReasons(response.miscellaneous_expense_types);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const monthString = month.toString().padStart(2, "0");
    const params = {
      month: monthString,
      year: year,
    };

    fetchMonthlyExpenseReport(params);
  }, [month, year]);

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));

    setMonth(month);
    setYear(year);
    const monthString = month.toString().padStart(2, "0");
    setApiLoading(true);
    const params = {
      month: monthString,
      year: year,
    };

    try {
      const fetchExpenseResponse = await fetchMonthlyExpenseReport(params);
      if (fetchExpenseResponse.success) {
        setFetchApiResponse(fetchExpenseResponse);
        const fetchData =
          fetchExpenseResponse.user_expense_summary.daily_expenses;

        const mappedApiData = fetchData.map((expense: ExpenseRowData) => ({
          id: expense.id,
          uploadedFiles: expense.expense_uploaded_files ?? [],
          date: expense.reporting_date,
          workingBrick: expense.brick_name ?? "-",
          station: expense.brick_type ?? "-",
          locality: expense.city_category ?? "-",
          totalDistanceKm: parseFloat(expense.total_distance_travel_km),
          dailyAllowance: parseFloat(expense.daily_allowance),
          travelAllowance: parseFloat(expense.traveling_allowance),
          fixAllowance: expense.fix_allowance
            ? parseFloat(expense.fix_allowance)
            : null,
          foodAllowance: expense.food_allowance
            ? parseFloat(expense.food_allowance)
            : null,
          hillStationAllowance: expense.hill_station_allowance
            ? parseFloat(expense.hill_station_allowance)
            : null,
          attendanceType: expense.attendance_type ?? "-",
          total: Number(expense.system_calculated_amount).toFixed(2) ?? null,
          status: expense.status,
          approvedStatus: expense.approved_status,
          systemRemarks: expense.system_remarks ?? "",
          remark: expense.remark ?? "",
          expectedAmount: expense.expected_amount
            ? parseFloat(expense.expected_amount)
            : null,
          totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
          miscellaneousExpenseType: expense.miscellaneous_expense_type ?? null,
          miscellaneousExpenseAmount:
            expense.miscellaneous_expense_amount ?? null,
        }));
        setTableApiData(mappedApiData);
      }
    } catch {
    } finally {
      setApiLoading(false);
    }
  };

  const clearData = async () => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    setMonth(month);
    setYear(year);

    const monthString = month.toString().padStart(2, "0");
    setApiLoading(true);
    const params = {
      month: monthString,
      year: year,
    };
    try {
      const fetchExpenseResponse = await fetchMonthlyExpenseReport(params);
      if (fetchExpenseResponse.success) {
        setFetchApiResponse(fetchExpenseResponse);
        const fetchData =
          fetchExpenseResponse.user_expense_summary.daily_expenses;

        const mappedApiData = fetchData.map((expense: ExpenseRowData) => ({
          id: expense.id,
          uploadedFiles: expense.expense_uploaded_files ?? [],
          date: expense.reporting_date,
          workingBrick: expense.brick_name ?? "-",
          station: expense.brick_type ?? "-",
          locality: expense.city_category ?? "-",
          totalDistanceKm: parseFloat(expense.total_distance_travel_km),
          dailyAllowance: parseFloat(expense.daily_allowance),
          travelAllowance: parseFloat(expense.traveling_allowance),
          fixAllowance: expense.fix_allowance
            ? parseFloat(expense.fix_allowance)
            : null,
          foodAllowance: expense.food_allowance
            ? parseFloat(expense.food_allowance)
            : null,
          attendanceType: expense.attendance_type ?? "-",
          total: Number(expense.system_calculated_amount).toFixed(2) ?? null,
          status: expense.status,
          approvedStatus: expense.approved_status,
          systemRemarks: expense.system_remarks ?? "",
          remark: expense.remark ?? "",
          expectedAmount: expense.expected_amount
            ? parseFloat(expense.expected_amount)
            : null,
          totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
          miscellaneousExpenseType: expense.miscellaneous_expense_type ?? null,
          miscellaneousExpenseAmount:
            expense.miscellaneous_expense_amount ?? null,
        }));
        setTableApiData(mappedApiData);
      }
    } catch {
    } finally {
      setApiLoading(false);
    }
  };

  const updateAddRemarks = (index: number, rowData: any) => {
    const existing = tableApiData.find((r) => r.id === rowData.id);

    const newRemark: AddRemark = {
      id: existing?.id || 0,
      reporting_date: rowData.date,
      remark: rowData.remark || "",
      status: rowData.status,
      expected_amount: rowData.expectedAmount || null,
    };

    const miscType = rowData.miscellaneousExpenseType;
    const miscAmount = rowData.miscellaneousExpenseAmount;

    const isValidType =
      miscType !== undefined && miscType !== null && miscType !== 0;
    const isValidAmount =
      miscAmount !== undefined && miscAmount !== null && miscAmount > 0;

    if (isValidType && isValidAmount) {
      newRemark.miscellaneous_expense_type_id = miscType;
      newRemark.miscellaneous_expense_amount = miscAmount;
    }

    setAddRemarks((prev) => {
      const exists = prev.find((r) => r.id === rowData.id);
      if (exists) {
        return prev.map((r) => (r.id === rowData.id ? newRemark : r));
      } else {
        return [...prev, newRemark];
      }
    });
  };

  const handleCommentChange = (index: number, value: string) => {
    const updatedData = [...tableApiData];
    updatedData[index].remark = value;
    setTableApiData(updatedData);

    setTouchedRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        remark: true,
      },
    }));

    const trimmedComment = value.trim();
    const isIncorrect = updatedData[index].status === "incorrect";

    setRowErrors((prev) => {
      const newErrors = { ...prev };

      if (isIncorrect && !trimmedComment) {
        // Set remark error
        newErrors[index] = {
          ...newErrors[index],
          remark: "Comment cannot be empty.",
        };
      } else {
        // Clear remark error
        if (newErrors[index]) {
          delete newErrors[index].remark;

          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
      }

      return newErrors;
    });

    if (isIncorrect && checkRowAccess()) {
      updateAddRemarks(index, updatedData[index]);
    }
  };

  const handleStatusChange = (
    index: number,
    value: "correct" | "incorrect"
  ) => {
    const access = {
      is_remark_locked: fetchApiResponse?.user_expense_summary.is_remark_locked,
      is_remark_submitted:
        fetchApiResponse?.user_expense_summary.is_remark_submitted,
      button_access: userDetails.button_access,
    };

    const hasAccess =
      !access.is_remark_locked &&
      !access.is_remark_submitted &&
      access.button_access === 1;

    const currentRow = tableApiData[index];

    if (!hasAccess) {
      if (value === "incorrect") {
        const remarkError = !currentRow.remark?.trim()
          ? "Comment cannot be empty."
          : "";
        const amountError =
          currentRow.expectedAmount == null || currentRow.expectedAmount <= 0
            ? "Amount must be greater than 0."
            : "";

        setRowErrors((prev) => ({
          ...prev,
          [index]: {
            remark: remarkError,
            amount: amountError,
          },
        }));
      }

      handleOpenAccessModal();
      return;
    }
    const updatedRow = { ...currentRow, status: value };
    const updatedData = [...tableApiData];
    updatedData[index] = {
      ...updatedData[index],
      status: value,
      ...(value === "correct" && {
        remark: "",
        expectedAmount: null,
        miscellaneousExpenseType: null,
        miscellaneousExpenseAmount: null,
      }),
    };

    setTableApiData(updatedData);

    if (value === "correct") {
      setRowErrors((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setTouchedRows((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setMiscErrors((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      updateAddRemarks(index, updatedRow);
      return;
    }

    const remark = updatedData[index].remark;
    const expectedAmount = updatedData[index].expectedAmount;

    const remarkError = !remark?.trim() ? "Comment cannot be empty." : "";
    const amountError =
      expectedAmount == null || expectedAmount <= 0
        ? "Amount must be greater than 0."
        : "";

    setRowErrors((prev) => ({
      ...prev,
      [index]: {
        remark: remarkError,
        amount: amountError,
      },
    }));

    updateAddRemarks(index, updatedRow);
  };

  const checkRowAccess = (): boolean => {
    return (
      !fetchApiResponse?.user_expense_summary.is_remark_locked &&
      !fetchApiResponse?.user_expense_summary.is_remark_submitted &&
      userDetails.button_access === 1
    );
  };

  const handleExpenseStatusChange = (index: number, value: number) => {
    const updatedData = [...tableApiData];
    updatedData[index].miscellaneousExpenseType = value;

    if (value === 0) {
      updatedData[index].miscellaneousExpenseAmount = undefined;
      setMiscErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } else {
      const amount = updatedData[index].miscellaneousExpenseAmount;
      if (amount === undefined || amount === null || amount <= 0) {
        setMiscErrors((prev) => ({
          ...prev,
          [index]: "Please enter amount greater than 0.",
        }));
      } else {
        setMiscErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }

    setTableApiData(updatedData);
    updateAddRemarks(index, updatedData[index]);
  };

  const handleExpectedAmountChange = (index: number, value: string) => {
    const updatedData = [...tableApiData];
    const amount = value ? parseFloat(value) : null;
    updatedData[index].expectedAmount = amount;

    const misc =
      parseFloat(updatedData[index].miscellaneousExpenseAmount as any) || 0;
    updatedData[index].totalAmount = (amount || 0) + misc;

    setTableApiData(updatedData);
    calculateOverallTotal(updatedData);

    setTouchedRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        amount: true,
      },
    }));

    const isIncorrect = updatedData[index].status === "incorrect";
    const expectedAmount = updatedData[index].expectedAmount;

    setRowErrors((prev) => {
      const newErrors = { ...prev };

      if (isIncorrect && (expectedAmount == null || expectedAmount <= 0)) {
        newErrors[index] = {
          ...newErrors[index],
          amount: "Amount must be greater than 0.",
        };
      } else {
        if (newErrors[index]) {
          delete newErrors[index].amount;
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
      }

      return newErrors;
    });

    if (isIncorrect && checkRowAccess()) {
      updateAddRemarks(index, updatedData[index]);
    }
  };

  const handleExpenseExtraAmountChange = (
    index: number,
    value: number | null
  ) => {
    const updatedData = [...tableApiData];
    const amount = value !== null ? parseFloat(value.toString()) : undefined;
    updatedData[index].miscellaneousExpenseAmount = amount;

    const expected = parseFloat(updatedData[index].expectedAmount as any) || 0;
    updatedData[index].totalAmount = expected + (amount || 0);

    const type = updatedData[index].miscellaneousExpenseType;

    if (type && (amount === undefined || amount <= 0)) {
      setMiscErrors((prev) => ({
        ...prev,
        [index]: "Please enter amount greater than 0.",
      }));
    } else {
      setMiscErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }

    setTableApiData(updatedData);
    calculateOverallTotal(updatedData);

    if (checkRowAccess()) {
      updateAddRemarks(index, updatedData[index]);
    }
  };

  const calculateOverallTotal = (data: ExpenseRow[]) => {
    let expectedTotal = 0;
    let systemTotal = 0;

    data.map((row) => {
      const expectedAmount = row.expectedAmount ?? null;
      const miscAmount = Number(row.miscellaneousExpenseAmount) || 0;
      const systemAmt = parseFloat(row.total) || 0;

      systemTotal += systemAmt;

      if (expectedAmount !== null && expectedAmount !== undefined) {
        expectedTotal += Number(expectedAmount) + miscAmount;
      } else {
        expectedTotal += systemAmt + miscAmount;
      }
    });

    setOverallTotalAmount(parseFloat(expectedTotal.toFixed(2)));
    setSystemTotalAmount(parseFloat(systemTotal.toFixed(2)));
  };

  const exportToExcel = () => {
    const exportData = tableApiData.map(({ remark, ...rest }) => ({
      ...rest,
      remark: remark || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");

    const userName = fetchApiResponse?.user_expense_summary.user_name ?? "";
    const formattedMonth = month.toString().padStart(2, "0");
    const fileName = `${userName.replace(
      /\s+/g,
      "_"
    )}_Expense_Report_${formattedMonth}_${year}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };
  const hasValidationErrors = () => {
    return Object.values(rowErrors).some((rowError) =>
      Object.values(rowError).some((error) => error && error.trim() !== "")
    );
  };

  useEffect(() => {
    if (!fetchApiResponse?.user_expense_summary.is_remark_submitted) {
      setShowExpectedAmountColumn(true);
      setShowTotalFinalAmount(false);
      setShowMiscellaneousAmountColumn(true);
    } else {
      setShowExpectedAmountColumn(false);
      setShowTotalFinalAmount(true);
      setShowMiscellaneousAmountColumn(false);
    }
  }, [fetchApiResponse]);

  const hasAnyErrors = useMemo(() => {
    return (
      Object.keys(rowErrors).length > 0 || Object.keys(miscErrors).length > 0
    );
  }, [rowErrors, miscErrors]);

  const handleSubmit = async () => {
    const newErrors: Record<number, { remark?: string; amount?: string }> = {};
    let hasManualErrors = false;

    tableApiData.forEach((row, index) => {
      if (row.status === "incorrect") {
        const rowError: { remark?: string; amount?: string } = {};

        if (!row.remark || row.remark.trim() === "") {
          rowError.remark = "Remark cannot be empty.";
        }

        if (!row.expectedAmount || row.expectedAmount <= 0) {
          rowError.amount = "Amount must be greater than 0.";
        }

        if (
          row.miscellaneousExpenseType &&
          (row.miscellaneousExpenseAmount === 0 ||
            row.miscellaneousExpenseAmount === undefined)
        ) {
          setMiscErrors((prev) => ({
            ...prev,
            [index]: "Miscellaneous amount must be greater than 0.",
          }));
          rowError.amount = "Miscellaneous amount must be greater than 0.";
        }

        if (rowError.remark || rowError.amount) {
          hasManualErrors = true;
          row.is_error = true;
          newErrors[index] = rowError;
        }
      }
    });

    if (hasManualErrors) {
      setRowErrors((prev) => ({ ...prev, ...newErrors }));
      const firstErrorIndex = Object.keys(newErrors)[0];
      const ref = rowRefs.current[Number(firstErrorIndex)];
      if (ref) {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.focus?.();
      }

      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please resolve all errors in the table before submitting.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    const incorrectRecords = addRemarks.filter(
      (record) => record.status === "incorrect"
    );

    const correctRecordsFiltered = addRemarks
      .filter(
        (record) =>
          record.status === "correct" &&
          record.miscellaneous_expense_type_id !== undefined &&
          record.miscellaneous_expense_amount !== undefined
      )
      .map((record) => ({
        id: record.id,
        reporting_date: record.reporting_date,
        status: record.status,
        miscellaneous_expense_type_id: record.miscellaneous_expense_type_id,
        miscellaneous_expense_amount: record.miscellaneous_expense_amount,
      }));

    const combinedPayload = [...correctRecordsFiltered, ...incorrectRecords];

    if (isChecked && !isSubmitted) {
      const finalPayload = {
        month: month.toString().padStart(2, "0"),
        year: year,
        add_remarks: combinedPayload,
      };

      const params = {
        month: month.toString().padStart(2, "0"),
        year: year,
      };

      try {
        Swal.fire({
          title: "Submitting...",
          text: "Please wait while we process your request.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const addCommentResponse = await addCommentAmountExpenseReport(
          finalPayload
        );

        if (!addCommentResponse?.success) {
          throw new Error("Failed to submit remarks.");
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
        Swal.close();

        const fetchExpenseResponse = await fetchMonthlyExpenseReport(params);

        if (fetchExpenseResponse.success) {
          setFetchApiResponse(fetchExpenseResponse);

          const isSubmitted =
            fetchExpenseResponse.user_expense_summary.is_remark_submitted;
          setIsDownloadAllowed(isSubmitted);

          const fetchData =
            fetchExpenseResponse.user_expense_summary.daily_expenses;

          const mappedApiData = fetchData.map((expense: ExpenseRowData) => {
            const mapped: any = {
              id: expense.id,
              uploadedFiles: expense.expense_uploaded_files ?? [],
              date: expense.reporting_date,
              workingBrick: expense.brick_name ?? "-",
              station: expense.brick_type ?? "-",
              locality: expense.city_category ?? "-",
              totalDistanceKm: parseFloat(expense.total_distance_travel_km),
              dailyAllowance: parseFloat(expense.daily_allowance),
              travelAllowance: parseFloat(expense.traveling_allowance),
              fixAllowance: expense.fix_allowance
                ? parseFloat(expense.fix_allowance)
                : null,
              foodAllowance: expense.food_allowance
                ? parseFloat(expense.food_allowance)
                : null,
              total: Number(expense.system_calculated_amount).toFixed(2),
              status: expense.status,
              approvedStatus: expense.approved_status ?? "-",
              systemRemarks: expense.system_remarks ?? "-",
              remark: expense.remark ?? "",
              expectedAmount: expense.expected_amount
                ? parseFloat(expense.expected_amount)
                : null,
              totalFinalAmount: Number(expense.final_amount).toFixed(2) ?? 0,
            };

            const hasMiscType =
              expense.miscellaneous_expense_type?.toString().trim() !== "";
            const hasMiscAmount =
              expense.miscellaneous_expense_amount !== null &&
              expense.miscellaneous_expense_amount !== undefined;

            if (hasMiscType || hasMiscAmount) {
              mapped.miscellaneousExpenseType =
                expense.miscellaneous_expense_type;
              mapped.miscellaneousExpenseAmount =
                expense.miscellaneous_expense_amount;
            }

            return mapped;
          });

          setTableApiData(mappedApiData);
          setShowTotalFinalAmount(isSubmitted);
          setShowExpectedAmountColumn(!isSubmitted);
          setShowMiscellaneousAmountColumn(!isSubmitted);

          Swal.fire({
            icon: "success",
            title: "Submitted!",
            text: "Your remarks have been successfully submitted and the report has been generated. You can download the Excel.",
            confirmButtonColor: "#1976d2",
          });
        }
      } catch (error: any) {
        Swal.close();
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            error?.message ||
            "There was an error processing your request. Please try again.",
          confirmButtonColor: "#d33",
        });
      } finally {
        setIsSubmitted(false);
      }
    }
  };

  const handleConfirmationModal = () => setIsConfirmationSubmitModalOpen(true);
  const handleConfirmationCloseModal = () =>
    setIsConfirmationSubmitModalOpen(false);

  const handleOpenAccessModal = () => setNoAccessModalOpen(true);
  const handleCloseAccessModal = () => setNoAccessModalOpen(false);

  const handleSystemRemarkOpenModal = (remarks: string) => {
    setSelectedSystemRemarks(remarks);
    setOpenSystemRemarkModal(true);
  };

  const handleSystemRemarkCloseModal = () => {
    setOpenSystemRemarkModal(false);
    setSelectedSystemRemarks("");
  };

  const handleSubmitConfirm = () => {
    handleSubmit();
    handleConfirmationCloseModal();
  };

  const tableHeaders = [
    "Date",
    "Working Brick",
    "Station",
    "Locality",
    "Total Distance (km)",
    "Daily Allowance",
    "Travel Allowance",
    "Attendance Type",
    "System Total Amount",
    "Approved Status",
    "Status",
    "System Remarks",
    "Remarks",
    showExpectedAmountColumn
      ? "Expected Amount"
      : showMiscellaneousAmountColumn
      ? ""
      : "Total Final Amount",
    "Expense Reason",
    "Expense Extra Amount",
    // "Document Upload",
  ];

  const visibleHeaders = tableHeaders.filter((header) => {
    if (header === "") return false;

    if (!showTotalFinalAmount && header === "Total Final Amount") return false;

    if (showTotalFinalAmount && header === "Expense Extra Amount") return false;

    return true;
  });

  const isTableEmpty = tableApiData.length === 0;

  return (
    <>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
        mt={1}
        gap={1}
      >
        {Object.keys(userDetails).length > 0 ? (
          <ReportFiltersComponent
            type={ReportScreen.EXPENSE_DASHBOARD}
            successCallback={filterData}
            clearSuccessCallBack={clearData}
            userRole={userRole.role}
            userDetails={userDetails}
            gridColumns={14}
            territoryMultiSelect={false}
          />
        ) : (
          <ReportFiltersLoading />
        )}

        <Button
          variant="outlined"
          color="secondary"
          size="small"
          sx={{
            width: "170px",
            height: "36px",
            color: "#1976d2",
            borderColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1976d2",
              color: "#fff",
              borderColor: "#1976d2",
            },
          }}
          onClick={exportToExcel}
          disabled={
            !isHeadquarterManager &&
            (isSubmitted ||
              !fetchApiResponse?.user_expense_summary.is_remark_submitted ||
              (month === currentDate.getMonth() + 1 &&
                year === currentDate.getFullYear()) ||
              isTableEmpty)
          }
        >
          Download Excel
        </Button>
      </Box>
      <Box sx={{ maxHeight: "70vh", mt: 1 }}>
        {month === currentDate.getMonth() + 1 &&
        year === currentDate.getFullYear() ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 4,
            }}
          >
            <Typography
              variant="h3"
              color="textSecondary"
              sx={{ textAlign: "center", mt: 6 }}
            >
              Expense for the {currentMonthName} {year} is not yet available.{" "}
              <br />
              You can check expense for {prevMonthName} {year}
            </Typography>
          </Box>
        ) : !apiLoading && isTableEmpty ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 4,
            }}
          >
            <Typography
              variant="h6"
              color="error"
              sx={{ textAlign: "center", mt: 6, fontSize: "30px" }}
            >
              For you, the Expense is not calculated. Since You Might have
              missed the daily Reporting.
            </Typography>
          </Box>
        ) : (
          <>
            {apiLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  my: 4,
                }}
              >
                <CircularProgress size={100} />
              </Box>
            ) : (
              <>
                <ExpenseSummaryCard
                  month={month}
                  year={year}
                  data={fetchApiResponse}
                />
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "70vh",
                    borderRadius: 4,
                    boxShadow: 2,
                    border: "1px solid #ccc",
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          height: 10,
                        }}
                      >
                        {visibleHeaders.map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              backgroundColor: "#d9e2ec",
                              fontWeight: 700,
                              fontSize: "0.6rem",
                              color: "#333",
                              position: "sticky",
                              top: 0,
                              height: 10,
                              zIndex: 1,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              paddingTop: "0.5px",
                              paddingBottom: "0.5px",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableApiData &&
                        tableApiData.length > 0 &&
                        tableApiData.map((row, index) => (
                          <TableRow
                            key={row.date}
                            ref={(el) => (rowRefs.current[index] = el)}
                            hover
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "#fafafa",
                                height: 20,
                              },
                            }}
                          >
                            <TableCell
                              sx={{ minWidth: 50, textAlign: "right" }}
                            >
                              {new Date(row.date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                              })}
                            </TableCell>

                            <TableCell sx={commonTableStyle}>
                              {row.workingBrick ?? "-"}
                            </TableCell>

                            <TableCell sx={commonTableStyle}>
                              {row.station ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.locality ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.totalDistanceKm}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.dailyAllowance}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.travelAllowance}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.attendanceType ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.total ?? "-"}
                            </TableCell>
                            <TableCell sx={commonTableStyle}>
                              {row.approvedStatus ?? "-"}
                            </TableCell>
                            <TableCell sx={{ minWidth: 80 }}>
                              <Select
                                size="small"
                                value={row.status || ""}
                                onChange={(e) =>
                                  handleStatusChange(
                                    index,
                                    e.target.value as "correct" | "incorrect"
                                  )
                                }
                                disabled={
                                  !!row.systemRemarks ||
                                  fetchApiResponse?.user_expense_summary
                                    .is_remark_locked ||
                                  fetchApiResponse?.user_expense_summary
                                    .is_remark_submitted ||
                                  userDetails.button_access !== 1
                                }
                                displayEmpty
                                fullWidth
                                sx={{
                                  backgroundColor: "#fff",
                                  fontSize: "0.7rem",
                                  height: 20,
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    minHeight: "unset",
                                  },
                                  "& fieldset": {
                                    borderRadius: 1,
                                  },
                                }}
                                renderValue={(selected) => {
                                  let color = "black";
                                  if (selected === "correct") color = "green";
                                  else if (selected === "incorrect")
                                    color = "red";
                                  return (
                                    <span style={{ color }}>
                                      {selected || "Select Status"}
                                    </span>
                                  );
                                }}
                              >
                                <MenuItem
                                  value="correct"
                                  sx={{ color: "green", fontSize: "0.7rem" }}
                                >
                                  correct
                                </MenuItem>
                                <MenuItem
                                  value="incorrect"
                                  sx={{ color: "red", fontSize: "0.7rem" }}
                                >
                                  incorrect
                                </MenuItem>
                              </Select>
                              <AccessCheckStatusModal
                                open={noAccessModalOpen}
                                onClose={handleCloseAccessModal}
                              />
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "1px 2px",
                                fontSize: "0.75rem",
                                lineHeight: 1.0,
                                textAlign: "center",
                              }}
                            >
                              {row.systemRemarks ? (
                                <>
                                  <IconButton
                                    color="warning"
                                    size="medium"
                                    onClick={() =>
                                      handleSystemRemarkOpenModal(
                                        row.systemRemarks
                                      )
                                    }
                                    sx={{
                                      fontSize: "0.5rem",
                                      height: "15px",
                                      width: "15px",
                                    }}
                                  >
                                    <WarningIcon fontSize="small" />
                                  </IconButton>

                                  <MissingLocationStatusModal
                                    open={openSystemRemarkModal}
                                    onClose={handleSystemRemarkCloseModal}
                                    systemRemarks={selectedSystemRemarks}
                                  />
                                </>
                              ) : (
                                "-"
                              )}
                            </TableCell>

                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              {row.status === "incorrect" &&
                                (!checkRowAccess() ||
                                  userDetails.button_access !== 1) && (
                                  <Box
                                    onClick={handleOpenAccessModal}
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      zIndex: 10,
                                      cursor: "not-allowed",
                                    }}
                                  />
                                )}

                              <Tooltip
                                title={row.remark?.trim() || "No comment added"}
                                placement="top-start"
                                arrow
                                enterDelay={300}
                              >
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={row.remark || ""}
                                  onChange={(e) =>
                                    handleCommentChange(index, e.target.value)
                                  }
                                  placeholder="Add remark"
                                  disabled={
                                    !!row.systemRemarks ||
                                    row.status === "correct" ||
                                    !checkRowAccess() ||
                                    row.status !== "incorrect" ||
                                    fetchApiResponse?.user_expense_summary
                                      .is_remark_locked ||
                                    fetchApiResponse?.user_expense_summary
                                      .is_remark_submitted ||
                                    userDetails.button_access !== 1
                                  }
                                  sx={{
                                    backgroundColor:
                                      !!row.systemRemarks ||
                                      row.status === "correct" ||
                                      !checkRowAccess() ||
                                      row.status !== "incorrect" ||
                                      fetchApiResponse?.user_expense_summary
                                        .is_remark_locked ||
                                      fetchApiResponse?.user_expense_summary
                                        .is_remark_submitted ||
                                      userDetails.button_access !== 1
                                        ? "#f0f0f0"
                                        : "#fff",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      height: 20,
                                      fontSize: "0.8rem",
                                    },
                                    "& input": {
                                      padding: "4px 8px",
                                    },
                                  }}
                                  error={!!rowErrors[index]?.remark}
                                  helperText={
                                    rowErrors[index]?.remark
                                      ? rowErrors[index]?.remark
                                      : ""
                                  }
                                />
                              </Tooltip>
                            </TableCell>
                            {showExpectedAmountColumn && (
                              <TableCell
                                sx={{ minWidth: 100, position: "relative" }}
                              >
                                {row.status === "incorrect" &&
                                  (!checkRowAccess() ||
                                    userDetails.button_access !== 1) && (
                                    <Box
                                      onClick={handleOpenAccessModal}
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        zIndex: 10,
                                        cursor: "not-allowed",
                                      }}
                                    />
                                  )}

                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={row.expectedAmount ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d{0,4}(?:\.\d{0,2})?$/.test(value)) {
                                      handleExpectedAmountChange(index, value);
                                    }
                                  }}
                                  placeholder="Enter amount"
                                  disabled={
                                    !!row.systemRemarks ||
                                    row.status === "correct" ||
                                    !checkRowAccess() ||
                                    row.status !== "incorrect" ||
                                    fetchApiResponse?.user_expense_summary
                                      .is_remark_locked ||
                                    fetchApiResponse?.user_expense_summary
                                      .is_remark_submitted ||
                                    userDetails.button_access !== 1
                                  }
                                  sx={{
                                    backgroundColor:
                                      row.status === "correct" ||
                                      !checkRowAccess() ||
                                      row.status !== "incorrect" ||
                                      fetchApiResponse?.user_expense_summary
                                        .is_remark_locked ||
                                      fetchApiResponse?.user_expense_summary
                                        .is_remark_submitted ||
                                      userDetails.button_access !== 1
                                        ? "#f0f0f0"
                                        : "#fff",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      height: 20,
                                      fontSize: "0.8rem",
                                    },
                                    "& input": {
                                      padding: "4px 8px",
                                    },
                                  }}
                                  error={!!rowErrors[index]?.amount}
                                  helperText={
                                    rowErrors[index]?.amount
                                      ? rowErrors[index]?.amount
                                      : ""
                                  }
                                />
                              </TableCell>
                            )}

                            {showTotalFinalAmount && (
                              <TableCell sx={commonTableStyle}>
                                {row.totalFinalAmount ?? 0}
                              </TableCell>
                            )}
                            <TableCell
                              sx={{ minWidth: 100, position: "relative" }}
                            >
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                  backgroundColor: "#fff",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                    height: 20,
                                    fontSize: "0.8rem",
                                  },
                                  "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                  },
                                }}
                              >
                                <Select
                                  displayEmpty
                                  value={row.miscellaneousExpenseType ?? ""}
                                  disabled={
                                    !!row.systemRemarks || !checkRowAccess()
                                  }
                                  onChange={(e) =>
                                    handleExpenseStatusChange(
                                      index,
                                      Number(e.target.value)
                                    )
                                  }
                                  renderValue={(selected) => {
                                    if (!selected || selected === 0) {
                                      return (
                                        <em style={{ color: "#aaa" }}>
                                          Select status
                                        </em>
                                      );
                                    }

                                    const foundReason = expenseReasons.find(
                                      (r) => r.id === Number(selected)
                                    );
                                    return foundReason
                                      ? foundReason.name
                                      : `${selected}`;
                                  }}
                                >
                                  <MenuItem value={0}>
                                    <em>Select status</em>
                                  </MenuItem>

                                  {expenseReasons.length > 0 ? (
                                    expenseReasons
                                      .filter((reason) => {
                                        // Hide "Meeting" if attendance type is Meeting
                                        return !(
                                          row.attendanceType === "Meeting" &&
                                          reason.name === "Meeting"
                                        );
                                      })
                                      .map((reason) => (
                                        <MenuItem
                                          key={reason.id}
                                          value={reason.id}
                                        >
                                          {reason.name}
                                        </MenuItem>
                                      ))
                                  ) : (
                                    <MenuItem disabled>
                                      <em>Loading reasons...</em>
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </TableCell>

                            {showMiscellaneousAmountColumn && (
                              <TableCell
                                sx={{ minWidth: 100, position: "relative" }}
                              >
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  placeholder="Enter extra amount"
                                  value={row.miscellaneousExpenseAmount ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (
                                      /^$|^(?:\d{1,4})(?:\.\d{0,2})?$/.test(
                                        value
                                      )
                                    ) {
                                      handleExpenseExtraAmountChange(
                                        index,
                                        value === "" ? null : Number(value)
                                      );
                                    }
                                  }}
                                  disabled={
                                    !!row.systemRemarks ||
                                    !row.miscellaneousExpenseType ||
                                    !checkRowAccess()
                                  }
                                  sx={{
                                    backgroundColor:
                                      row.miscellaneousExpenseType &&
                                      checkRowAccess()
                                        ? "#fff"
                                        : "#f0f0f0",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      height: 20,
                                      fontSize: "0.8rem",
                                    },
                                    "& input": {
                                      padding: "4px 8px",
                                    },
                                  }}
                                  error={!!miscErrors[index]}
                                  helperText={miscErrors[index]}
                                />
                              </TableCell>
                            )}
                            {/* <TableCell sx={commonTableStyle}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={{ padding: "1px 4px", maxHeight: "22px" }}
                                onClick={() =>
                                  row.id !== undefined && handleOpenMyModal(row)
                                }
                                disabled={
                                  isSubmitted || 
                                  Number(row.totalDistanceKm) <= 100 ||
                                  row.id === undefined ||
                                  !checkRowAccess()
                                }
                              >
                                Upload
                              </Button>
                              {selectedRow?.id === row.id && (
                                <FileUploadReadModal
                                  open={openMyModal}
                                  onClose={handleCloseMyModal}
                                  rowId={selectedRow?.id ?? null}
                                  uuids={selectedRow?.uuids ?? []}
                                  onSuccess={refreshTableAfterModalClose}
                                />
                              )}
                            </TableCell> */}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={2}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        disabled={isSubmitted || !checkRowAccess()}
                      />
                    }
                    label={
                      <Typography variant="body1">
                        I hereby declare that the information provided is true
                        and correct.
                      </Typography>
                    }
                  />
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      isHeadquarterManager ||
                      !isChecked ||
                      isSubmitted ||
                      !checkRowAccess() ||
                      isTableEmpty ||
                      hasAnyErrors ||
                      isOver15Percent
                    }
                    onClick={handleConfirmationModal}
                  >
                    Submit
                  </Button>
                  <SubmitConfirmationModal
                    open={isConfirmationSubmitModalOpen}
                    onClose={handleConfirmationCloseModal}
                    onConfirm={handleSubmitConfirm}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default UserExpenseReport;
